/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Optional autoplay: when the element has `data-autoplay="<seconds>"`, it advances one page
 * on that interval, wraps from the end back to the start (arrows wrap too), pauses while
 * hovered or focused, honours a `[data-carousel-toggle]` pause/play button, and never
 * starts when the visitor prefers reduced motion.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel [data-autoplay]>
 *     [<button data-carousel-toggle> / <button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");
    this.toggleButton = this.querySelector("[data-carousel-toggle]");

    if (!this.track) return;

    const seconds = Number(this.dataset.autoplay);
    this.autoplayMs = seconds > 0 ? seconds * 1000 : 0;
    this.wraps = this.autoplayMs > 0;
    this.userPaused = false;
    this.hoverPaused = false;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    if (this.autoplayMs) this.setupAutoplay();

    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.teardownAutoplay();
  }

  setupAutoplay() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (this.reducedMotion.matches) this.setUserPaused(true);

    this.onPauseHover = () => {
      this.hoverPaused = true;
      this.stop();
    };
    this.onResumeHover = () => {
      this.hoverPaused = false;
      this.start();
    };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.onResumeHover();
    };
    this.onToggle = () => this.setUserPaused(!this.userPaused);

    this.addEventListener("pointerenter", this.onPauseHover);
    this.addEventListener("pointerleave", this.onResumeHover);
    this.addEventListener("focusin", this.onPauseHover);
    this.addEventListener("focusout", this.onFocusOut);
    this.toggleButton?.addEventListener("click", this.onToggle);

    this.start();
  }

  teardownAutoplay() {
    this.stop();
    this.removeEventListener("pointerenter", this.onPauseHover);
    this.removeEventListener("pointerleave", this.onResumeHover);
    this.removeEventListener("focusin", this.onPauseHover);
    this.removeEventListener("focusout", this.onFocusOut);
    this.toggleButton?.removeEventListener("click", this.onToggle);
  }

  start() {
    this.stop();
    if (!this.autoplayMs || this.userPaused || this.hoverPaused) return;
    this.timer = window.setInterval(() => this.scrollByPage(1), this.autoplayMs);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  /** @param {boolean} paused */
  setUserPaused(paused) {
    this.userPaused = paused;
    if (this.toggleButton) {
      this.toggleButton.setAttribute("aria-pressed", String(paused));
      const label = paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
      if (label) this.toggleButton.setAttribute("aria-label", label);
      this.toggleButton.querySelector("[data-icon-pause]")?.classList.toggle("hidden", paused);
      this.toggleButton.querySelector("[data-icon-play]")?.classList.toggle("hidden", !paused);
    }
    if (paused) this.stop();
    else this.start();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * Wrapping carousels jump to the opposite end when already at an edge.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.wraps && maxScroll > 1) {
      if (direction > 0 && this.track.scrollLeft >= maxScroll - 1) {
        this.track.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }
      if (direction < 0 && this.track.scrollLeft <= 1) {
        this.track.scrollTo({ left: maxScroll, behavior: "smooth" });
        return;
      }
    }
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end (never for wrapping carousels). */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || (!this.wraps && atStart);
    if (this.nextButton) this.nextButton.disabled = !overflowing || (!this.wraps && atEnd);
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
